import * as React from 'react';

import { getCmsLocaleString } from 'js/lib/language';
import { simplifyQueryValue } from 'js/lib/queryUtils';
import { useLocation } from 'js/lib/useRouter';
import user from 'js/lib/user';

export type OrgLandingContextState = {
  isPreview: boolean;
  locale: string;
  thirdPartyOrganization: {
    id: string;
    slug: string;
    loginMethod?: string | null;
  };
  enableOrgHomepageAutoJoinProgram: boolean;
};

const OrgLandingContext = React.createContext<OrgLandingContextState>({
  isPreview: false,
  locale: 'en',
  thirdPartyOrganization: {
    id: '',
    slug: '',
    loginMethod: null,
  },
  enableOrgHomepageAutoJoinProgram: false,
});

type Props = Omit<OrgLandingContextState, 'isPreview' | 'locale'>;

export function OrgLandingProvider({
  thirdPartyOrganization,
  enableOrgHomepageAutoJoinProgram,
  children,
}: React.PropsWithChildren<Props>) {
  const { query } = useLocation();

  const isPreview = Boolean(query.preview);
  const locale = simplifyQueryValue(query.locale) || getCmsLocaleString() || 'en-US';

  // TODO: this is a workaround to add Arabic support for the UNDP.
  // Remove this once we support `Accept-Language: ar` globally.
  React.useEffect(() => {
    // logged user reads locale from user profile, so we only check logged out user here
    if (!user.isAuthenticatedUser() && locale === 'ar-SA') {
      // set page to RTL
      document.documentElement.setAttribute('dir', 'rtl');

      // inject style to fix login modal RTL issue
      const style = document.createElement('style');
      style.innerHTML = `
        .rc-Modal .c-modal-content {
          left: 0;
          right: 50%;
          transform: translate(50%, -50%);
          -webkit-transform: translate(50%, -50%);
          -moz-transform: translate(50%, -50%);
          -o-transform: translate(50%, -50%);
          -ms-transform: translate(50%, -50%);
        }
      `;
      document.body.appendChild(style);
    }
  }, [locale]);

  return (
    <OrgLandingContext.Provider
      value={{
        isPreview,
        locale,
        thirdPartyOrganization,
        enableOrgHomepageAutoJoinProgram,
      }}
    >
      {children}
    </OrgLandingContext.Provider>
  );
}

export default OrgLandingContext;
